import { useEffect, useState } from "react";
import { Navigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "@/lib/api";
import { inviteAcceptedResponse } from "@sitebrew/contracts/v1";
import { keys } from "@/lib/query-keys";
import { ButtonLink } from "@/components/button";
import { LoginCard, LoginCardSubtitle, LoginCardTitle, Notice } from "@/components/ui/login-card";

/**
 * Where an invite e-mail's link lands (`sitebrew-api`'s `/v1/invites/accept`
 * link, design note 0001 §4/§6, M3).
 *
 * The route wrapping this screen already requires a session (same
 * `beforeLoad` shape as `consentRoute`) — a signed-out visitor is sent to
 * `/login` with this address as `next` and comes back here once they are. So
 * by the time this screen renders, the only unresolved questions are the
 * ones the API's `acceptInvite` itself answers: is the token still good, and
 * does it name *this* signed-in person's e-mail.
 */

const FAILURE_SENTENCES: Record<string, string> = {
  not_found: "This invite link isn't valid.",
  revoked: "This invite has been withdrawn by the workspace owner.",
  expired: "This invite has expired — ask the workspace owner to send a new one.",
  workspace_gone: "The workspace this invite was for no longer exists.",
  already_used: "This invite has already been used.",
  wrong_identity:
    "This invite was sent to a different e-mail address than the one you're signed in with.",
  email_not_verified: "Verify your e-mail address before accepting an invite.",
};

type Status =
  | { kind: "pending" }
  | { kind: "done"; workspaceId: string }
  | { kind: "failed"; message: string };

export function InviteAcceptScreen({ token }: { token: string | undefined }) {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<Status>({ kind: "pending" });

  useEffect(() => {
    if (!token) {
      setStatus({ kind: "failed", message: "This invite link is missing its token." });
      return;
    }

    let current = true;
    api("/invites/accept", inviteAcceptedResponse, { method: "POST", body: { token } })
      .then((answered) => {
        if (!current) return;
        // The account now belongs to one more workspace than `/me` last said —
        // the switcher and the sidebar both read from that cache, and without
        // this the new membership is real at the API and invisible here.
        void queryClient.invalidateQueries({ queryKey: keys.me() });
        setStatus({ kind: "done", workspaceId: answered.workspaceId });
      })
      .catch((err) => {
        if (!current) return;
        const code = err instanceof ApiError ? err.code : undefined;
        setStatus({
          kind: "failed",
          message:
            (code && FAILURE_SENTENCES[code]) ??
            (err instanceof ApiError ? err.message : "This invite could not be accepted."),
        });
      });
    return () => {
      current = false;
    };
  }, [token, queryClient]);

  if (status.kind === "done") {
    return (
      <Navigate to="/workspaces/$workspaceId/sites" params={{ workspaceId: status.workspaceId }} replace />
    );
  }

  if (status.kind === "failed") {
    return (
      <LoginCard>
        <LoginCardTitle>Couldn't join the workspace</LoginCardTitle>
        <Notice>{status.message}</Notice>
        <ButtonLink className="mt-[.9rem] w-full" href="/">Back to your websites</ButtonLink>
      </LoginCard>
    );
  }

  return (
    <LoginCard>
      <LoginCardTitle>Joining workspace…</LoginCardTitle>
      <LoginCardSubtitle>One moment.</LoginCardSubtitle>
    </LoginCard>
  );
}
